import React, { useEffect } from 'react';
import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';
import { version as globalStylesVersion } from '@marriott/global-styles/package.json';

import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import path from 'path';
import { canUseDOM } from '@marriott/mi-ui-library';
import { CustomModelClient } from '@marriott/mi-headless-utils';
import '../styles/style.css';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  useAuthStore,
  useLegalGenericStore,
  ErrorHandlerAlert,
  updatePageSpecificDataLayer,
} from '@marriott/mi-legal-components';

import { defineComponentMapping } from '../import-components';
import { useRouter } from 'next/router';

path.resolve('./next.config.js');
declare global {
  interface Window {
    jQuery: unknown;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $: any;
    deployedEnvType: string;
  }
}

const paramsString = canUseDOM && window.location.search;
const searchParams = new URLSearchParams(paramsString);
const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync(!(searchParams?.get('wcmmode') === 'disabled') && { modelClient });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const App = function (props: any) {
  const router = useRouter();

  const { Component, pageProps } = props;
  const {
    model,
    cookies,
    isAuthorMode,
    pagePath,
    sessionData,
    NEXT_GLOBAL_ASSET_PREFIX,
    resolvedUrl,
    currentLocale,
    featureFlagLogo,
  } = pageProps;
  const locale = currentLocale ? currentLocale.replace('_', '-') : 'en-US';
  const allowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;
  const isRtl = pageProps?.model?.rtlEnable || pageProps?.rtlEnable || false; // Default to LTR if not provided

  const rtlPluginTs = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };

  // load global styles on the basis of dir present in props
  if (isRtl) {
    import('@marriott/global-styles/dist/marriot.global.rtl.css');
  } else {
    import('@marriott/global-styles/dist/marriot.global.css');
  }

  defineComponentMapping(allowedComponents, pagePath, isAuthorMode, featureFlagLogo);

  useEffect(() => {
    useAuthStore.getState().setAuthCookie(cookies);
    useLegalGenericStore.getState().setLanguage(model?.language || 'en-US');
    useLegalGenericStore.getState().setIsAuthorMode(isAuthorMode);
    useLegalGenericStore.getState().setSessionData(sessionData);
    useLegalGenericStore.getState().setLocale(locale);

    if (canUseDOM && !isAuthorMode && resolvedUrl) {
      updatePageSpecificDataLayer(resolvedUrl);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthorMode) {
      let remoteURL: URL | string;
      try {
        remoteURL = new URL(document.body.dataset.remoteUrl || '');
        remoteURL = remoteURL.origin;
      } catch (e) {
        remoteURL = '';
      }
      const assetUrl = `${
        process.env['NEXT_PUBLIC_ASSET_DOMAIN'] || remoteURL || 'https://www.marriott.com'
      }${NEXT_GLOBAL_ASSET_PREFIX}`;

      const globalStyle = document.createElement('link');

      globalStyle.rel = 'stylesheet';
      globalStyle.href =
        assetUrl +
        (isRtl
          ? `global-styles/${globalStylesVersion}/marriot.global.rtl.css`
          : `global-styles/${globalStylesVersion}/marriot.global.css`);
      document.head.appendChild(globalStyle);

      const { asPath } = router;
      const path = asPath.split('/').filter(Boolean);
      const lastSegment = path[path.length - 1];

      const brandCodeMatch = lastSegment?.match(/brandCode=([^&]+)/);
      const brandCode = brandCodeMatch ? brandCodeMatch[1] : '';

      if (brandCode) {
        const brandLink = document.createElement('link');

        brandLink.rel = 'stylesheet';
        brandLink.href = assetUrl + `brand-framework/brand-config.${brandCode}.css`;

        document.head.appendChild(brandLink);
      }
    }
  }, []);

  Object.defineProperty(rtlPluginTs, 'name', { value: 'rtlPluginMonorepo' });

  return (
    <ErrorHandlerAlert>
      <StyleSheetManager stylisPlugins={isRtl ? [rtlPluginTs] : []}>
        <Component {...pageProps} />
      </StyleSheetManager>
    </ErrorHandlerAlert>
  );
};

export default App;
